import React, { useState, useEffect, useRef } from 'react'
import {
  Workflow,
  WNode,
  WNodeAction,
  WTransition,
  WManualTransition,
  WScheduledTransition,
  WScheduledDateTransition,
  WTimeoutTransition,
  WScheduledBusinessDateTransition,
} from './model'
import * as R from 'ramda'

// const mermaidAPI = require('mermaid')
import * as dagreD3 from 'dagre-d3'
import * as d3 from 'd3'

import renderGraph from './graph-d3-render'

import cx from 'classnames'

interface GraphProps {
  orderId: string
  workflow: Workflow
  currentNode: string
  onSelectNode?: (node: WNode) => void
  onSelectTransition?: (transition: WTransition) => void
  width: number
  height: number
  externalActions: ExternalAction[]
  getNodeOptions?: (n: WNode) => D3NodeOptions
  getTransitionOptions?: (t: WTransition) => D3TransitionOptions
}

export interface D3NodeOptions {
  shape?: 'ellipse' | 'rect'
  class?: string
}
export interface D3TransitionOptions {
  class?: string
}

export type Maybe<T> = T | null

interface ExternalAction {
  name: string
  type: string
  workflow_nodes: string[]
  outcomes: {
    success: Maybe<string>
    failure: Maybe<string>
    accept: Maybe<string>
    reject: Maybe<string>
  }
}

export const BezierCurve = (context: any) => {
  let data: number[][] = []
  return {
    lineStart: () => (data = []),
    point: (x: number, y: number) => data.push([x, y]),
    lineEnd: () => {
      const [x0, y0] = data[0]
      const [cp1x, cp1y] = data[1]
      const [cp2x, cp2y] = data[data.length - 2]
      const [x1, y1] = data[data.length - 1]
      context.moveTo(x0, y0)
      context.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x1, y1)
    },
  }
}

// function toEdgeId(t: WTransition): string {
//   return `${t.from_node}${t.to_node}${t.transition_type}`
// }

function toTransitionLabel(t: WTransition): string {
  switch (t.transition_type) {
    case 'manual':
      const mt = t as WManualTransition
      return mt.name
    case 'scheduled':
      const st = t as WScheduledTransition
      return `s: ${secondsToTimeDuration(Math.abs(st.time.time_offset))} ${
        st.time.time_offset < 0 ? 'before' : 'after'
      } ${st.time.ref}`
    case 'scheduled_date':
      const sd = t as WScheduledDateTransition
      return `sd: ${formatDayOffset(sd.time.ref, sd.time.day_offset)}@${formatTime(sd.time.time_offset)}`
    case 'timer':
      const stt = t as WTimeoutTransition
      return `t: ${secondsToTimeDuration(stt.time.time_offset)}`
    case 'scheduled_business_date':
      const sbd = t as WScheduledBusinessDateTransition
      return `sbd: ${formatDayOffset(sbd.time.ref, sbd.time.day_offset)}@${formatTime(sbd.time.time_offset)}`
    default:
      return `${t.transition_type}`
  }
}

function formatDayOffset(ref: string, days: number): string {
  if (days != 0) return `${Math.abs(days)}d ${days < 0 ? 'before' : 'after'} ${ref}`
  else return `the day of ${ref}`
}

const pad = R.curry((fill: string, count: number, s: string) =>
  R.join('', R.append(s, R.map(R.always(fill), R.range(0, count / fill.length - s.length))))
)

function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const mins = Math.floor(seconds / 60) - hours * 60
  const secs = seconds % 60
  const numbers = [hours, mins, secs]
  return R.join(':', R.map(R.compose(pad('0', 2), R.toString), numbers))
}

function secondsToTimeDuration(seconds: number): string {
  const days =  Math.floor(seconds / 86400)
  const hours = Math.floor((seconds - days * 86400) / 3600)
  const mins =  Math.floor((seconds - days * 86400 - hours * 3600 ) / 60)
  const secs =  Math.floor((seconds - days * 86400 - hours * 3600 - mins * 60))
  const suf = ['d', 'h', 'm', 's']
  const numbers = [days, hours, mins, secs]

  return R.join(
    ' ',
    R.map(
      ([n, lndx]: [number, number]) => `${n}${suf[lndx]}`,
      R.filter(
        ([n, _l]: [number, number]) => n != 0,
        R.transpose<number>([numbers, [0, 1, 2, 3]])
      )
    )
  )
}

const createNodeActionLabel = (node: WNode, externalActions: ExternalAction[]): HTMLDivElement => {
  const svg_label = document.createElement('div')
  const svg_name = document.createElement('p')
  svg_name.innerText = node.name
  svg_label.appendChild(svg_name)
  svg_label.setAttribute('class', 'node-label')
  const actions = document.createElement('div')
  actions.setAttribute('class', 'node-actions')
  appendNodeActions(actions, node.actions)
  appendNodeExternalActions(actions, externalActions)
  // actions.innerText = R.join(', ', R.map(R.prop('name'), R.flatten([node.actions, node.actions, node.actions])))
  svg_label.appendChild(actions)
  return svg_label
}
const createIcon = (el: HTMLDivElement, label: Maybe<string>, iconClass: string): void => {
  if (label) {
    const icon = document.createElement('i')
    icon.setAttribute('class', iconClass)
    icon.setAttribute('title', label)
    el.appendChild(icon)
  }
}
const appendNodeActions = (actions: HTMLDivElement, nodeActions: WNodeAction[]): void => {
  nodeActions.reduce((parent: HTMLDivElement, action: WNodeAction) => {
    const el = document.createElement('div')
    el.setAttribute('class', 'ow-order-action')
    el.innerText = `${action.name} ${actionParam(action)}`
    createIcon(el, action.success, 'fa fa-flag text-success')
    createIcon(el, action.failure, 'fa fa-flag text-error')
    el.setAttribute('title', `on success: ${action.success || '-nothing-'}\non failure: ${action.failure || 'nothing'}`)
    parent.appendChild(el)
    return parent
  }, actions)
}

const actionParam = (action: WNodeAction): string => {
  switch (action.name) {
    case 'Mark':
      return 'as ' + R.defaultTo('unknown', R.path(['params', 'state'], action))
    case 'SubscribePayment':
    case 'SubscribeRDS':
      return 'for ' + R.defaultTo('unknown', R.path(['params', 'event'], action))
    case 'NotifyPOS':
    case 'NotifyLoyalty':
      return 'say ' + R.defaultTo('unknown', R.path(['params', 'verb'], action))
    case 'RestaurantServiceEmail':
    case 'RestaurantServiceSMS':
    case 'RestaurantServiceFax':
    case 'ChannelEmail':
    case 'ChannelSMS':
    case 'UserEmail':
    case 'UserSMS':
      return 'w/t ' + R.defaultTo('unknown', R.path(['params', 'template'], action))
    default:
      return ''
  }
}

const appendNodeExternalActions = (actions: HTMLDivElement, nodeActions: ExternalAction[]): void => {
  nodeActions.reduce((parent: HTMLDivElement, action: ExternalAction) => {
    const el = document.createElement('div')
    el.setAttribute('class', 'ow-notification')
    el.innerText = `${action.type}: ${action.name}`
    createIcon(el, action.outcomes.success, 'fa fa-flag text-success')
    createIcon(el, action.outcomes.failure, 'fa fa-flag text-error')
    createIcon(el, action.outcomes.accept, 'fa fa-hand-up text-succcess')
    createIcon(el, action.outcomes.reject, 'fa fa-hand-down text-error')
    el.setAttribute(
      'title',
      `
        on success: ${action.outcomes.success || '-nothing-'}
        on failure: ${action.outcomes.failure || '-nothing-'}
        on accept: ${action.outcomes.accept || '-nothing-'}
        on reject: ${action.outcomes.reject || '-nothing-'}
      `
    )
    parent.appendChild(el)
    return parent
  }, actions)
}

export default function Graph({
  orderId,
  workflow,
  currentNode,
  onSelectNode,
  onSelectTransition,
  width,
  height,
  externalActions,
  getNodeOptions,
  getTransitionOptions,
}: GraphProps): JSX.Element {
  const nodeTreeGroup = useRef(null)
  const nodeTree = useRef(null)
  const [d3Graph, setGraph] = useState<dagreD3.graphlib.Graph | null>(null)

  useEffect(() => {
    const graph = new dagreD3.graphlib.Graph({ multigraph: true }) // multigraph
    graph.setGraph({ marginx: 10, marginy: 10, height: 1000 })

    workflow.nodes.forEach((node: WNode) => {
      const options = {
        rx: 5,
        ry: 5,
        label: createNodeActionLabel(
          node,
          externalActions.filter((a: ExternalAction) => a.workflow_nodes.includes(node.id))
        ),
        shape: 'rect',
        class: cx({
          visited: false,
          current: false,
        }),
      }
      graph.setNode(node.id, R.merge(options, getNodeOptions ? getNodeOptions(node) : {}))
    })

    workflow.transitions.forEach((edge: WTransition, index: number) => {
      const options = {
        label: toTransitionLabel(edge),
        labelStyle: 'font-style: italic;',
        curve: BezierCurve,
        class: cx('workflow', edge.transition_type, edge.category || '', {
          visited: false,
        }),
      }
      graph.setEdge(
        { v: edge.from_node, w: edge.to_node, name: index.toString() },
        R.mergeWith((a, b) => a + ' ' + b, options, getTransitionOptions ? getTransitionOptions(edge) : {})
      )
    })

    setGraph(graph)
  }, [orderId, workflow, currentNode])

  const onSelectNodeById = (id: string) => {
    const node = workflow.nodes.find(R.propEq('id', id))
    if (node && onSelectNode) onSelectNode(node)
  }

  const onSelectEdgeById = (id: string) => {
    const edge = workflow.transitions[parseInt(id)]
    if (edge && onSelectTransition) onSelectTransition(edge)
  }

  useEffect(() => {
    renderGraph(nodeTree.current, nodeTreeGroup.current, d3Graph, onSelectNodeById, onSelectEdgeById)
  }, [d3Graph])

  return (
    <svg className="dagre-d3" ref={nodeTree} width={width} height={height}>
      <g ref={nodeTreeGroup} />
    </svg>
  )
}
